import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Search, Pencil, Trash2, Upload, Download } from "lucide-react";
import { Link } from "react-router-dom";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import CategoryBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { REGIONS_DATA, PROVINSI_LIST } from "@/constants/indonesia_regions";

const EMPTY = { nama: "", alamat: "", provinsi: "", kabupaten: "", no_hp: "", pic: "", status: "", catatan: "" };

export default function Pesantren() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 20;
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const fileRef = useRef(null);

  const catByName = useMemo(() => {
    const m = {};
    categories.forEach((c) => (m[c.name] = c));
    return m;
  }, [categories]);

  const loadCategories = async () => {
    const { data } = await api.get("/categories");
    setCategories(data);
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (q) params.q = q;
      if (statusFilter !== "ALL") params.status = statusFilter;
      const { data } = await api.get("/pesantren", { params });
      setItems(data.items);
      setTotal(data.total);
      setTotalPages(data.total_pages);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [statusFilter, page]);

  useEffect(() => {
    setPage(1);
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const openCreate = () => {
    if (categories.length === 0) {
      toast.error("Buat kategori terlebih dahulu di Pengaturan.");
      return;
    }
    setEditing(null);
    setForm({ ...EMPTY, status: categories[0].name });
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      nama: p.nama,
      alamat: p.alamat,
      provinsi: p.provinsi || "",
      kabupaten: p.kabupaten || "",
      no_hp: p.no_hp || "",
      pic: p.pic,
      status: p.status,
      catatan: p.catatan || "",
    });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.status) {
      toast.error("Pilih kategori");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/pesantren/${editing.id}`, form);
        toast.success("Objek Mad’u diperbarui");
      } else {
        await api.post("/pesantren", form);
        toast.success("Objek Mad’u ditambahkan");
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/pesantren/${deleteTarget.id}`);
      toast.success("Objek Mad’u dihapus");
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const doImport = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/pesantren/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.inserted} objek diimpor`);
      if (data.errors?.length) {
        toast.warning(`${data.errors.length} baris gagal: ${data.errors.slice(0, 2).join("; ")}`);
      }
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const doExport = async () => {
    try {
      const token = localStorage.getItem("rabtah_token");
      const res = await fetch(`${API_BASE}/pesantren/export/csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export gagal");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "objek-madu.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("File CSV diunduh");
    } catch (err) {
      toast.error("Gagal export");
    }
  };

  const renderBadge = (statusName, testid) => {
    const cat = catByName[statusName];
    return <CategoryBadge name={statusName} polarity={cat?.polarity} testid={testid} />;
  };

  return (
    <div className="p-6 md:p-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6 md:mb-10">
        <div>
          <div className="label-overline">Direktori</div>
          <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95] mt-2">
            Objek Mad’u
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            data-testid="import-file-input"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) doImport(f);
              e.target.value = "";
            }}
          />
          <Button
            variant="outline"
            data-testid="import-btn"
            className="rounded-none border-black hover:bg-black hover:text-white"
            onClick={() => fileRef.current?.click()}
          >
            <Upload size={14} className="mr-2" /> Import Excel
          </Button>
          <Button
            variant="outline"
            data-testid="export-btn"
            className="rounded-none border-black hover:bg-black hover:text-white"
            onClick={doExport}
          >
            <Download size={14} className="mr-2" /> Export CSV
          </Button>
          <Button
            data-testid="add-pesantren-btn"
            className="rounded-none bg-black text-white hover:bg-white hover:text-black hover:border hover:border-black"
            onClick={openCreate}
          >
            <Plus size={14} className="mr-2" /> Tambah
          </Button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input
            data-testid="pesantren-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari nama pesantren / ormas / tokoh..."
            className="pl-10 h-11 rounded-none border-black"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="pesantren-status-filter" className="w-full md:w-56 h-11 rounded-none border-black">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="ALL">Semua Kategori</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c.id} value={c.name}>
                {c.name} ({c.polarity})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="label-overline text-center py-16">Memuat...</div>
      ) : items.length === 0 ? (
        <div className="border border-neutral-200 p-12 text-center">
          <div className="font-display font-bold text-xl mb-2">Belum ada data</div>
          <div className="text-sm text-neutral-500 mb-4">
            Tambah objek pertama atau import dari file Excel.
          </div>
          <div className="text-xs text-neutral-400">
            Kolom Excel: <span className="font-mono">nama, alamat, pic, status</span> (wajib), <span className="font-mono">no_hp, catatan</span> (opsional). Nilai <span className="font-mono">status</span> harus sesuai kategori yang ada di <Link to="/pengaturan" className="underline">Pengaturan</Link>.
          </div>
        </div>
      ) : (
        <>
        <div className="border border-neutral-200" data-testid="pesantren-list">
          <div className="hidden md:block">
            <table className="w-full">
              <thead>
                <tr className="border-b border-neutral-200 text-left">
                  <th className="label-overline p-4">Nama Pesantren / Ormas / Tokoh</th>
                  <th className="label-overline p-4">Alamat</th>
                  <th className="label-overline p-4">PIC</th>
                  <th className="label-overline p-4">No HP</th>
                  <th className="label-overline p-4">Kategori</th>
                  <th className="label-overline p-4 text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`row-${p.id}`}>
                    <td className="p-4 font-semibold">{p.nama}</td>
                    <td className="p-4 text-sm text-neutral-600 max-w-xs">
                      <div>{p.alamat}</div>
                      {(p.kabupaten || p.provinsi) && (
                        <div className="text-xs text-neutral-400 mt-0.5">
                          {[p.kabupaten, p.provinsi].filter(Boolean).join(", ")}
                        </div>
                      )}
                    </td>
                    <td className="p-4 text-sm">{p.pic}</td>
                    <td className="p-4 text-sm">{p.no_hp || "-"}</td>
                    <td className="p-4">{renderBadge(p.status, `status-${p.id}`)}</td>
                    <td className="p-4 text-right">
                      <button
                        data-testid={`edit-${p.id}`}
                        onClick={() => openEdit(p)}
                        className="p-2 hover:bg-black hover:text-white transition-colors duration-150"
                        aria-label="Edit"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        data-testid={`delete-${p.id}`}
                        onClick={() => setDeleteTarget(p)}
                        className="p-2 hover:bg-black hover:text-white transition-colors duration-150"
                        aria-label="Hapus"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden divide-y divide-neutral-200">
            {items.map((p) => (
              <div key={p.id} className="p-4" data-testid={`card-${p.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0 flex-1 pr-2">
                    <div className="font-display font-bold text-base tracking-tight">{p.nama}</div>
                    <div className="text-xs text-neutral-500 mt-1">{p.alamat}</div>
                  </div>
                  {renderBadge(p.status, `status-mobile-${p.id}`)}
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs mt-3">
                  <div><span className="label-overline block">PIC</span>{p.pic}</div>
                  <div><span className="label-overline block">HP</span>{p.no_hp || "-"}</div>
                </div>
                {p.catatan && <div className="text-xs text-neutral-500 mt-3 border-t border-neutral-100 pt-2">{p.catatan}</div>}
                <div className="flex gap-2 mt-3">
                  <button
                    data-testid={`edit-mobile-${p.id}`}
                    onClick={() => openEdit(p)}
                    className="flex-1 border border-black py-2 text-xs uppercase tracking-wider hover:bg-black hover:text-white transition-colors duration-150"
                  >
                    Edit
                  </button>
                  <button
                    data-testid={`delete-mobile-${p.id}`}
                    onClick={() => setDeleteTarget(p)}
                    className="flex-1 border border-black py-2 text-xs uppercase tracking-wider hover:bg-black hover:text-white transition-colors duration-150"
                  >
                    Hapus
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="label-overline" data-testid="pesantren-total">
            Menampilkan {items.length} dari {total} objek
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="pesantren-prev-page"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="border border-black px-3 py-2 text-xs uppercase tracking-wider disabled:opacity-30 hover:bg-black hover:text-white transition-colors duration-150 disabled:hover:bg-transparent disabled:hover:text-black"
            >
              Sebelumnya
            </button>
            <div className="text-xs px-3" data-testid="pesantren-page-info">
              Halaman <span className="font-semibold">{page}</span> / {totalPages}
            </div>
            <button
              data-testid="pesantren-next-page"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="border border-black px-3 py-2 text-xs uppercase tracking-wider disabled:opacity-30 hover:bg-black hover:text-white transition-colors duration-150 disabled:hover:bg-transparent disabled:hover:text-black"
            >
              Berikutnya
            </button>
          </div>
        </div>
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none border-black max-w-lg max-h-[90vh] overflow-y-auto" data-testid="pesantren-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl tracking-tight">
              {editing ? "Edit Objek Mad’u" : "Tambah Objek Mad’u"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="label-overline">Nama Pesantren / Ormas / Tokoh *</Label>
              <Input
                required
                data-testid="input-nama"
                value={form.nama}
                onChange={(e) => setForm({ ...form, nama: e.target.value })}
                className="mt-1 rounded-none border-black h-11"
              />
            </div>
            <div>
              <Label className="label-overline">Alamat *</Label>
              <Textarea
                required
                data-testid="input-alamat"
                value={form.alamat}
                onChange={(e) => setForm({ ...form, alamat: e.target.value })}
                className="mt-1 rounded-none border-black"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="label-overline">Provinsi</Label>
                <Select
                  value={form.provinsi}
                  onValueChange={(v) => {
                    setForm({ ...form, provinsi: v, kabupaten: "" });
                  }}
                >
                  <SelectTrigger data-testid="input-provinsi" className="mt-1 rounded-none border-black h-11">
                    <SelectValue placeholder="Pilih Provinsi" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none max-h-60 overflow-y-auto">
                    {PROVINSI_LIST.map((prov) => (
                      <SelectItem key={prov} value={prov}>
                        {prov}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-overline">Kabupaten / Kota</Label>
                <Select
                  disabled={!form.provinsi}
                  value={form.kabupaten}
                  onValueChange={(v) => setForm({ ...form, kabupaten: v })}
                >
                  <SelectTrigger data-testid="input-kabupaten" className="mt-1 rounded-none border-black h-11 disabled:opacity-50">
                    <SelectValue placeholder={form.provinsi ? "Pilih Kabupaten / Kota" : "Pilih Provinsi dahulu"} />
                  </SelectTrigger>
                  <SelectContent className="rounded-none max-h-60 overflow-y-auto">
                    {(REGIONS_DATA[form.provinsi] || []).map((kab) => (
                      <SelectItem key={kab} value={kab}>
                        {kab}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-overline">PIC *</Label>
                <Input
                  required
                  data-testid="input-pic"
                  value={form.pic}
                  onChange={(e) => setForm({ ...form, pic: e.target.value })}
                  className="mt-1 rounded-none border-black h-11"
                />
              </div>
              <div>
                <Label className="label-overline">No HP</Label>
                <Input
                  data-testid="input-no-hp"
                  value={form.no_hp}
                  onChange={(e) => setForm({ ...form, no_hp: e.target.value })}
                  className="mt-1 rounded-none border-black h-11"
                />
              </div>
            </div>
            <div>
              <Label className="label-overline">Kategori *</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="input-status" className="mt-1 rounded-none border-black h-11">
                  <SelectValue placeholder="Pilih kategori" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.name}>
                      {c.name} ({c.polarity})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[0.65rem] text-neutral-500 mt-1">
                Belum ada kategori yang cocok? Tambah di <Link to="/pengaturan" className="underline">Pengaturan</Link>.
              </div>
            </div>
            <div>
              <Label className="label-overline">Catatan</Label>
              <Textarea
                data-testid="input-catatan"
                value={form.catatan}
                onChange={(e) => setForm({ ...form, catatan: e.target.value })}
                className="mt-1 rounded-none border-black"
                rows={3}
              />
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                data-testid="pesantren-cancel"
                className="rounded-none border-black"
                onClick={() => setDialogOpen(false)}
              >
                Batal
              </Button>
              <Button
                type="submit"
                disabled={saving}
                data-testid="pesantren-save"
                className="rounded-none bg-black text-white hover:bg-white hover:text-black hover:border hover:border-black"
              >
                {saving ? "MENYIMPAN..." : "SIMPAN"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-none border-black">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-black tracking-tight">Hapus Objek Mad’u?</AlertDialogTitle>
            <AlertDialogDescription>
              Menghapus <strong>{deleteTarget?.nama}</strong> juga akan menghapus semua kunjungannya. Aksi ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel" className="rounded-none border-black">Batal</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm"
              onClick={confirmDelete}
              className="rounded-none bg-black text-white hover:bg-white hover:text-black hover:border hover:border-black"
            >
              HAPUS
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
